import { FileService } from 'core/services/FileService';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { useRouteId } from 'react-router/dist/lib/hooks';
import { AsyncState } from 'shared/libs/state';
import { ApiError } from 'shared/types';
import { ApplicantsSendEmailStore } from './job-applicants-send-email';
import { JobApplicantsApiService } from './JobApplicantsApiService';
import {
   ActiveOption,
   Candidate,
   JobAccdetailResponse,
   JobInfo,
   JobSeekers,
   UserDetails,
} from './types';

export class JobApplicantsStore {
   readonly loadAccDetailState = new AsyncState<JobAccdetailResponse>();
   readonly statusChangeState = new AsyncState<JobAccdetailResponse>();
   readonly addEmployerCommentState = new AsyncState<any>();
   readonly removeEmployerCommentState = new AsyncState<any>();
   readonly updateRadtingState = new AsyncState<any>();
   readonly downloadPdfState = new AsyncState<Blob>();

   readonly sendEmail: ApplicantsSendEmailStore;

   seletedCandidateId: null | number = null;

   rawApplicants: Candidate[] = [];
   userDetails: UserDetails | null = null;
   jobInfo: JobInfo | null = null;
   jobSeekers: JobSeekers | null = null;
   activeOptions: ActiveOption | null = null;
   selectedCandidates: Candidate[] = [];
   planDetails: string | null = '';

   showEmailDialog: boolean = false;

   constructor() {
      makeObservable(this, {
         seletedCandidateId: observable,
         planDetails: observable,
         rawApplicants: observable,
         userDetails: observable,
         jobSeekers: observable,
         jobInfo: observable,
         selectedCandidates: observable,
         showEmailDialog: observable,
         selectedAll: computed,
         showSendEmail: computed,
         statusOptions: computed,
         toggleEmailDialog: action,
         onSelectAll: action,
         onToggleCandidate: action,
         setSelectedCandidateId: action,
      });

      this.sendEmail = new ApplicantsSendEmailStore(this);
   }

   get selectedAll(): boolean {
      return (
         this.rawApplicants.length !== 0 &&
         this.selectedCandidates.length === this.rawApplicants.length
      );
   }

   get showSendEmail(): boolean {
      return this.selectedCandidates.length > 0;
   }

   get statusOptions(): { value: string; viewValue: string }[] {
      const activeOption = this.activeOptions;
      if (activeOption) {
         const options: { value: string; viewValue: string }[] = [
            { value: 'new', viewValue: 'Новий' },
            { value: 'reviewed', viewValue: 'Переглянуто' },
         ];
         const o = Object.entries(activeOption).map(([value, viewValue]) => ({ value, viewValue }));
         return [...o, ...options];
      }
      return [];
   }

   setSelectedCandidateId(id: number | null): void {
      this.seletedCandidateId = id;
   }

   onSelectAll(): void {
      this.selectedCandidates = this.rawApplicants.slice();
   }

   onDeselectAll(): void {
      this.selectedCandidates = [];
   }

   onToggleCandidate(cand: Candidate) {
      const candidate = this.selectedCandidates.find((c) => c.user_id === cand.user_id);

      if (candidate) {
         this.selectedCandidates = this.selectedCandidates.filter(
            (c) => c.user_id !== candidate.user_id
         );
      } else {
         this.selectedCandidates.push(cand);
      }
   }

   toggleEmailDialog(o: boolean): void {
      this.showEmailDialog = o;
   }

   async loadAccDetail(dto: { slug: string }): Promise<void> {
      await this.loadAccDetailState.run(() => JobApplicantsApiService.getAccDetail(dto), {
         onSuccess: (res) => {
            if (res.status === 200) {
               runInAction(() => {
                  this.planDetails = res.response.planDetails;
                  this.rawApplicants = res.response.candidates;
                  this.userDetails = res.response.userDetails;
                  this.jobSeekers = res.response.job_seekers;
                  this.jobInfo = res.response.jobInfo;
                  this.activeOptions = res.response.active_option;
               });
            }
         },
         onError: () => {},
      });
   }

   async onStatusChange(dto: {
      slug: string;
      userId: number;
      candidateId: number;
      status: string;
   }): Promise<void> {
      if (typeof dto.slug === 'string') {
         await this.statusChangeState.run(() => JobApplicantsApiService.changeStatus(dto), {
            onSuccess: (res) => {
               if (res.status === 200) {
                  runInAction(() => {
                     this.planDetails = res.response.planDetails;
                     this.rawApplicants = res.response.candidates;
                     this.userDetails = res.response.userDetails;
                     this.jobSeekers = res.response.job_seekers;
                     this.jobInfo = res.response.jobInfo;
                     this.activeOptions = res.response.active_option;
                  });
               }
            },
            onError: () => {},
         });
      }
   }

   async loadPdf(dto: { id: number; filename: string }): Promise<void> {
      await this.downloadPdfState.run(() => JobApplicantsApiService.getCandidatePdfCv(dto), {
         onSuccess: (blob) => {
            if (blob) {
               runInAction(() => {
                  FileService.pdf({ blob, url: '', filename: dto.filename });
               });
            }
         },
         onError: () => {},
      });
   }

   async addEmployerComment(
      dto: { id: number; comment: string; userId: number },
      { onSuccess, onError }: { onSuccess?: (res: any) => void; onError?: (e: string) => void } = {}
   ): Promise<void> {
      this.setSelectedCandidateId(dto.userId);
      await this.addEmployerCommentState.run(
         () =>
            JobApplicantsApiService.addEmployerComment({
               id: dto.id,
               comment: dto.comment,
            }),
         {
            onSuccess: (res) => {
               if (res) {
                  onSuccess?.(res);
               }
            },
            onError: (e: ApiError) => {
               if (e.details?.error?.message === 'validation.max.string') {
                  onError?.('Mаксимальна довжина коментаря 250 символів');
               }
            },
            onFinally: () => {
               this.setSelectedCandidateId(null);
            },
         }
      );
   }

   async removeEmployerComment(
      dto: { id: number; userId: number },
      { onSuccess, onError }: { onSuccess?: (res: any) => void; onError?: (e: string) => void } = {}
   ): Promise<void> {
      this.setSelectedCandidateId(dto.userId);
      await this.removeEmployerCommentState.run(
         () => JobApplicantsApiService.deleteEmployerComment({ id: dto.id }),
         {
            onSuccess: (res) => {
               if (res) {
                  onSuccess?.(res);
               }
            },
            onError: () => {},
            onFinally: () => {
               this.setSelectedCandidateId(null);
            },
         }
      );
   }

   async updateRating(
      dto: { id: number; val: number },
      { onSuccess, onError }: { onSuccess?: (res: any) => void; onError?: (e: string) => void } = {}
   ): Promise<void> {
      await this.updateRadtingState.run(() => JobApplicantsApiService.updateRating(dto), {
         onSuccess: (res) => {
            if (res) {
               onSuccess?.(res);
            }
         },
         onError: () => {},
      });
   }
}
